import { clampUsageLimit } from '../../utils/usageContract';
import {
  ApiResponse,
  AdminUser,
  AdminUsageItem,
  AdminUsageByModelItem,
  UsageReconciliationResponse,
  AdminTopupRequest,
  AdminAssignRoleRequest,
  Paginated,
  UsageTransactionsResponse,
  UsageHistoryResponse,
  UsageDetailResponse,
} from '../../types/api';
import { api } from './client';

// Admin endpoints
export const admin = {
  getUsers: async (params?: {
    limit?: number; // 1-100, default: 20
    offset?: number; // >=0, default: 0
    q?: string; // search by email or name
  }): Promise<ApiResponse<Paginated<AdminUser>>> => {
    const response = await api.get<ApiResponse<Paginated<AdminUser>>>('/admin/users', { params });
    return response.data;
  },

  getUsage: async (params?: {
    from?: string; // RFC3339, exclusive
    to?: string; // RFC3339, inclusive
    limit?: number; // 1-100, default: 20
    offset?: number; // >=0, default: 0
  }): Promise<ApiResponse<Paginated<AdminUsageItem>>> => {
    const response = await api.get<ApiResponse<Paginated<AdminUsageItem>>>('/admin/usage', { params });
    return response.data;
  },

  getUsageByUser: async (userId: string): Promise<ApiResponse<AdminUsageItem>> => {
    const response = await api.get<ApiResponse<AdminUsageItem>>(`/admin/usage/${userId}`);
    return response.data;
  },

  topupUser: async (userId: string, data: AdminTopupRequest): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(`/admin/users/${userId}/topup`, data);
    return response.data;
  },

  assignRole: async (data: AdminAssignRoleRequest): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>('/admin/users/assign-role', data);
    return response.data;
  },

  // High-level usage statistics for billing dashboard (legacy)
  getUsageStatistics: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<UsageTransactionsResponse>> => {
    const response = await api.get<ApiResponse<UsageTransactionsResponse>>('/admin/usage/transactions', { params });
    return response.data;
  },

  // New admin usage history
  getUsageHistory: async (params?: {
    limit?: number;
    offset?: number;
    from?: string;
    to?: string;
    user_id?: string;
    functionality?: string;
    status?: string;
    exclude_service_usage?: boolean;
  }): Promise<ApiResponse<UsageHistoryResponse>> => {
    const response = await api.get<ApiResponse<UsageHistoryResponse>>('/admin/usage/history', { params });
    return response.data;
  },

  getUsageDetail: async (
    functionality: string,
    entityId: string,
    params?: { user_id?: string }
  ): Promise<ApiResponse<UsageDetailResponse>> => {
    const response = await api.get<ApiResponse<UsageDetailResponse>>(
      `/admin/usage/history/${functionality}/${entityId}`,
      { params }
    );
    return response.data;
  },

  /**
   * AIT-258: разрез расхода по паре (пользователь, модель) за период.
   * Без `user_id` агрегирует по всем пользователям.
   */
  getUsageByModel: async (params?: {
    limit?: number;
    offset?: number;
    from?: string;
    to?: string;
    user_id?: string;
    exclude_service_usage?: boolean;
  }): Promise<ApiResponse<Paginated<AdminUsageByModelItem>>> => {
    const response = await api.get<ApiResponse<Paginated<AdminUsageByModelItem>>>('/admin/usage/by-model', {
      params: { ...params, limit: clampUsageLimit(params?.limit) },
    });
    return response.data;
  },

  /**
   * AIT-259: отчёт сверки ML-леджера с базой.
   * Ответ приходит в трёх разных формах — разбирается `parseReconciliation`.
   */
  getUsageReconciliation: async (): Promise<ApiResponse<UsageReconciliationResponse>> => {
    const response = await api.get<ApiResponse<UsageReconciliationResponse>>('/admin/usage/reconciliation');
    return response.data;
  },
};
